// Dependencies
import React from "react";

// Modules
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import Gallery from "react-photo-gallery";

// App
import { serverUrl, apiEndpoints } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import PostBody from "../../partials/postBody";
import { alertMessages } from "../../partials/alertMessages";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import CommentSection from "../../partials/comments/commentSection";
import Reactions from "../../partials/comments/reactions";
import { favoritesContentType } from "../../utils/helpers";
import CompanyappLoader from "../../partials/companyappLoader";

// UI components
import { Carousel, Row } from "react-bootstrap";
import { Typography, Button, Dialog, Box } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@mui/icons-material";
import SinglesHeader from "../../partials/singlesHeader";

import { getImageDimensions } from "./imageProcessor";

export default function GallerySingle(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [post, setPost] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [categoryName, setCategoryName] = useState(null);

  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [showViewer, setShowViewer] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewFullscreen, setViewFullscreen] = useState(false);

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const fullscreenRef = useRef(null);

  const hash = searchParams.get("hash");
  const contentType = favoritesContentType("gallery");

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (gallery.length > 0 && gallery.length === loadedImages) {
      setShowGallery(true);
    }
  }, [gallery, loadedImages]);

  useEffect(() => {
    if (post) {
      getCategory();
    }
    // eslint-disable-next-line
  }, [post]);

  /**
   * @function setData
   * @description Updates the state of the component upon data retrival
   * @param {object} _data
   * @param {object} _realm
   */

  const setData = (_data) => {
    setPost(_data);
    setIsError(false);
    getImageDimensions(_data.field_gallery, hash ?? null)
      .then((newGallery) => {
        setGallery(newGallery);
        setIsLoading(false);
      })
      .catch((error) => {
        setError(true, 0, "Error processing gallery");
        setIsLoading(false);
      });
    // getComments();
    // getReactions();
    // getCategory(_data.field_category);
  };

  /**
   * @function setError
   * @description Updates the state of the component upon an error
   * @param {boolean} _isError
   * @param {int} _errorStatus
   * @param {string} _errorMessage
   */
  const setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  const loadNode = () => {
    const nid = params.id;

    if (nid) {
      getContent(nid);
      // getComments(nid)
    } else {
      setError(true, 0, alertMessages.contentError);
    }
  };

  /**
   * @function getContent
   * @description Retrieves the data from an API / Fallback to local realm object if there is no connection
   */
  const getContent = (nid) => {
    let path = `${apiEndpoints.gallery}?nid=${nid}&status=1&promote=1&_format=json`;

    if (hash) {
      path = `/api/v2/shared?bundle=gallery&nid=${nid}&authkey=${hash}`;
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.data.rows.length > 0) {
          setData(response.data.rows[0]);
        } else {
          setIsLoading(false);
          setError(true, 200, alertMessages.noData);
        }
      })
      .catch((_error) => {
        if (_error.response) {
          setError(true, _error.response.status, _error.response.statusText);
        } else if (_error.request) {
          setError(true, 0, alertMessages.requestError.message);
        } else {
          setError(true, 0, alertMessages.unkownError.message);
        }
      });
  };

  const getCategory = (id) => {
    if (post.field_category) {
      getOnlineNode(
        // `api/categories?vid=news`,
        `api/categories?tid=${post.field_category}`,
        user.access_token
      )
        .then((response) => {
          let categoryTemp =
            response.data.length > 0 ? response.data[0] : { title: "" };
          setCategoryName(categoryTemp.title);
        })
        .catch((_error) => {
          if (_error.response) {
            setError(true, _error.response.status, _error.response.statusText);
          } else if (_error.request) {
            //console.log('@rest request: ', _error.request);
            setError(true, 0, alertMessages.requestError.message);
          } else {
            //console.log('@rest unknown: ', _error);
            setError(true, 0, alertMessages.unkownError.message);
          }
        });
    }
  };

  const galleryElement = ({ photo, index }) => {
    let imageSrc = photo.src;

    if (hash) {
      imageSrc = `${serverUrl}/shared/files/${photo.basename}?access_token=${photo.access_token}`;
    }

    return (
      <Button
        key={`button-${index}`}
        className="gallery-element"
        style={{
          width: photo.width,
          height: photo.height,
        }}
        onClick={() => {
          setActiveIndex(index);
          setShowViewer(true);
        }}
      >
        <img
          src={imageSrc}
          alt=""
          onLoad={() => setLoadedImages((prevData) => prevData + 1)}
          onError={() => setLoadedImages((prevData) => prevData + 1)}
        />
      </Button>
    );
  };

  const renderGallery = () => {
    return (
      <>
        {!showGallery && (
          <div className="gallery-opacity-loader">
            <CompanyappLoader />
          </div>
        )}
        <div
          className={`gallery-opacity-container ${showGallery ? "show" : ""}`}
        >
          <Gallery
            photos={gallery}
            targetRowHeight={180}
            renderImage={galleryElement}
          />
        </div>
      </>
    );
  };

  const enterFullscreen = () => {
    const element = fullscreenRef.current;

    if (element) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        // For Firefox
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        // For Chrome, Safari and Opera
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        // For IE/Edge
        element.msRequestFullscreen();
      }
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  if (isLoading) {
    return <SkeletonSingleScreen />;
  } else {
    if (isError) {
      return <Error status={errorStatus} message={errorMessage} />;
    } else {
      let category = "Uncategorised";

      if (categoryName) {
        category = categoryName;
      }

      if (post.categoryLabel) {
        category = post.categoryLabel;
      }

      return (
        <main className="galleries single">
          <article className="article">
            <SinglesHeader
              route={"/Galleries"}
              backBtnText={"Galleries"}
              category={category || "Uncategorised"}
              timestamp={post.created}
              title={post.title}
              hash={hash}
            />
            <PostBody body={post.body} />
            <Row className="gallery-container mt-4">{renderGallery()}</Row>
            <div className="article__body"></div>
            {user && user.current_user && (
              <>
                <Row className="reaction-container gx-0">
                  <Reactions
                    user={user}
                    nid={post.nid}
                    contentType={contentType}
                    field_allow_reactions={post.field_allow_reactions}
                    field_access_token={post.field_access_token}
                    title={post.title}
                    shareEnabled
                  />
                </Row>
                <CommentSection
                  nid={post.nid}
                  field_allow_reactions={post.field_allow_reactions}
                  field_allow_comments={post.field_allow_comments}
                />
              </>
            )}
          </article>
          <Dialog
            className="gallery-image-viewer"
            maxWidth="lg"
            fullWidth
            open={showViewer}
            onClose={() => {
              setShowViewer(false);
              setViewFullscreen(false);
            }}
            fullScreen
            ref={fullscreenRef}
            // fullScreen={fullScreen}
            // sx={{
            //   "& .MuiPaper-root": !fullScreen ? {
            //     width: '80%',
            //     height: '80%'
            //   } : {}
            // }}
          >
            <Button
              className="close-viewer-btn"
              onClick={() => {
                setShowViewer(false);
                setViewFullscreen(false);
              }}
            >
              <CloseRoundedIcon />
            </Button>
            <Button
              className="fullscreen-viewer-btn"
              onClick={() => {
                if (viewFullscreen) {
                  exitFullscreen();
                  setViewFullscreen(false);
                } else {
                  enterFullscreen();
                  setViewFullscreen(true);
                }
              }}
            >
              {viewFullscreen ? (
                <FullscreenExitOutlined />
              ) : (
                <FullscreenOutlined />
              )}
            </Button>
            <Carousel
              className="image-viewer-carousel"
              activeIndex={activeIndex}
              controls={true}
              indicators={false}
              slide={true}
              onSelect={(index) => setActiveIndex(index)}
              interval={null}
              touch={true}
            >
              {gallery.map((e, i) => {
                let imageSrc = e.src;

                if (hash) {
                  imageSrc = `${serverUrl}/shared/files/${e.basename}?access_token=${e.access_token}`;
                }

                return (
                  <Carousel.Item
                    key={`c-item-${i}`}
                    className="image-viewer-carousel-item"
                  >
                    <Box className="image-item-container">
                      <Box className="image-item-reducer">
                        <img
                          className="image-item"
                          src={imageSrc}
                          alt={e.title}
                          data-description={e.description}
                        />
                      </Box>
                      <Box className="image-caption-container">
                        <Typography
                          className="image-caption"
                          owner="admin"
                          variant="body2"
                          color="adminText.contrastText"
                          weight="regular"
                          align="center"
                        >
                          {e.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Carousel.Item>
                );
              })}
            </Carousel>
          </Dialog>
        </main>
      );
    }
  }
}
